<!-- frontend/src/views/public/TestTake.vue -->
<template>
  <div class="test-take">
    <div class="container py-5">
      <div class="row justify-content-center">
        <div class="col-lg-8">
          <!-- Startseite mit Formular für Bewerber-Daten -->
          <div v-if="step === 'intro'" class="test-intro">
            <div class="card shadow-lg">
              <div class="card-body p-4">
                <div class="text-center mb-4">
                  <img 
                    v-if="test && test.company && test.company.logo" 
                    :src="test.company.logo" 
                    :alt="test.company.name" 
                    class="company-logo mb-4"
                  />
                  <h1 class="card-title display-6 fw-bold mb-2 text-gradient">{{ test.name }}</h1>
                  <p class="text-muted">
                    {{ test.description }}
                  </p>
                </div>
                
                <div class="test-info mb-4">
                  <div class="row">
                    <div class="col-md-6">
                      <div class="info-item">
                        <i class="fas fa-clock"></i>
                        <span>Zeitlimit: {{ test.time_limit || 'Keine Begrenzung' }}</span>
                      </div>
                    </div>
                    <div class="col-md-6">
                      <div class="info-item">
                        <i class="fas fa-question-circle"></i>
                        <span>Fragen: {{ test.questions ? test.questions.length : 0 }}</span>
                      </div>
                    </div>
                  </div>
                </div>
                
                <applicant-form 
                  :test-id="testId" 
                  @submit="startTest" 
                  @cancel="goBack"
                />
              </div>
            </div>
          </div>
          
          <!-- Test mit Fragen -->
          <div v-else-if="step === 'test'" class="test-questions">
            <div class="card shadow-lg">
              <div class="card-body p-4">
                <div class="d-flex justify-content-between align-items-center mb-4">
                  <div>
                    <h1 class="h3 mb-0">{{ test.name }}</h1>
                    <p class="text-muted mb-0">Frage {{ currentQuestionIndex + 1 }} von {{ test.questions.length }}</p>
                  </div>
                  
                  <div class="timer" v-if="test.time_limit">
                    <i class="fas fa-clock me-2"></i>
                    <span>{{ formatTime(remainingTime) }}</span>
                  </div>
                </div>
                
                <div class="progress mb-4">
                  <div 
                    class="progress-bar" 
                    role="progressbar" 
                    :style="{ width: progressPercent + '%' }" 
                    :aria-valuenow="progressPercent" 
                    aria-valuemin="0" 
                    aria-valuemax="100"
                  >
                    {{ progressPercent }}%
                  </div>
                </div>
                
                <div class="question-content">
                  <h2 class="h4 mb-3">{{ currentQuestion.text }}</h2>
                  
                  <!-- Multiple Choice -->
                  <div v-if="currentQuestion.type === 'multiple_choice'" class="mt-4">
                    <div v-for="(option, index) in currentQuestion.options" :key="index" class="form-check mb-3">
                      <input 
                        class="form-check-input" 
                        type="radio" 
                        :name="'question_' + currentQuestion.id" 
                        :id="'option_' + index" 
                        :value="option.id"
                        v-model="userAnswers[currentQuestion.id]"
                      />
                      <label class="form-check-label" :for="'option_' + index">
                        {{ option.text }}
                      </label>
                    </div>
                  </div>
                  
                  <!-- Texteingabe -->
                  <div v-else-if="currentQuestion.type === 'text'" class="mt-4">
                    <textarea 
                      class="form-control" 
                      rows="5" 
                      v-model="userAnswers[currentQuestion.id]"
                      placeholder="Geben Sie Ihre Antwort hier ein..."
                    ></textarea>
                  </div>
                  
                  <!-- Ja/Nein -->
                  <div v-else-if="currentQuestion.type === 'boolean'" class="mt-4">
                    <div class="form-check mb-3">
                      <input 
                        class="form-check-input" 
                        type="radio" 
                        :name="'question_' + currentQuestion.id" 
                        id="option_true" 
                        :value="true"
                        v-model="userAnswers[currentQuestion.id]"
                      />
                      <label class="form-check-label" for="option_true">
                        Ja
                      </label>
                    </div>
                    <div class="form-check mb-3">
                      <input 
                        class="form-check-input" 
                        type="radio" 
                        :name="'question_' + currentQuestion.id" 
                        id="option_false" 
                        :value="false"
                        v-model="userAnswers[currentQuestion.id]"
                      />
                      <label class="form-check-label" for="option_false">
                        Nein
                      </label>
                    </div>
                  </div>
                </div>
                
                <div class="d-flex justify-content-between mt-4">
                  <button 
                    class="btn btn-outline-secondary" 
                    @click="previousQuestion" 
                    :disabled="currentQuestionIndex === 0"
                  >
                    <i class="fas fa-chevron-left me-2"></i>Zurück
                  </button>
                  
                  <button 
                    v-if="currentQuestionIndex < test.questions.length - 1" 
                    class="btn btn-primary" 
                    @click="nextQuestion"
                  >
                    Weiter<i class="fas fa-chevron-right ms-2"></i>
                  </button>
                  
                  <button 
                    v-else 
                    class="btn btn-success" 
                    @click="confirmSubmit"
                  >
                    Test abschließen<i class="fas fa-check ms-2"></i>
                  </button>
                </div>
              </div>
            </div>
            
            <!-- Navigation durch Fragen -->
            <div class="question-navigation mt-4">
              <div class="card shadow-sm">
                <div class="card-body p-3">
                  <div class="d-flex flex-wrap justify-content-center">
                    <button 
                      v-for="(question, index) in test.questions" 
                      :key="question.id"
                      class="btn question-nav-btn" 
                      :class="{
                        'btn-primary': index === currentQuestionIndex,
                        'btn-outline-primary': userAnswers[question.id] !== undefined && index !== currentQuestionIndex,
                        'btn-outline-secondary': userAnswers[question.id] === undefined && index !== currentQuestionIndex
                      }"
                      @click="goToQuestion(index)"
                    >
                      {{ index + 1 }}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <!-- Bestätigungsmodal vor Abgabe -->
          <div v-if="showSubmitConfirm" class="modal fade show d-block">
            <div class="modal-dialog">
              <div class="modal-content">
                <div class="modal-header">
                  <h5 class="modal-title">Test abschließen</h5>
                  <button type="button" class="btn-close" @click="showSubmitConfirm = false"></button>
                </div>
                <div class="modal-body">
                  <p>Sind Sie sicher, dass Sie den Test abschließen möchten?</p>
                  
                  <div v-if="unansweredCount > 0" class="alert alert-warning">
                    <i class="fas fa-exclamation-triangle me-2"></i>
                    Sie haben {{ unansweredCount }} Frage(n) noch nicht beantwortet.
                  </div>
                </div>
                <div class="modal-footer">
                  <button type="button" class="btn btn-secondary" @click="showSubmitConfirm = false">Zurück zum Test</button>
                  <button type="button" class="btn btn-success" @click="submitTest" :disabled="submitting">
                    <span class="spinner-border spinner-border-sm me-2" v-if="submitting"></span>
                    <i class="fas fa-check me-2" v-else></i>Test abschließen
                  </button>
                </div>
              </div>
            </div>
            <div class="modal-backdrop fade show"></div>
          </div>
          
          <!-- Abschlussseite -->
          <div v-else-if="step === 'completed'" class="test-completed">
            <div class="card shadow-lg text-center">
              <div class="card-body p-5">
                <div class="completion-icon mb-4">
                  <i class="fas fa-check-circle fa-5x text-success"></i>
                </div>
                
                <h1 class="display-6 fw-bold mb-3">Vielen Dank!</h1>
                <p class="lead mb-4">
                  Ihr Test wurde erfolgreich abgeschlossen und gespeichert.
                </p>
                
                <div class="alert alert-info mb-4">
                  <p class="mb-0">
                    <i class="fas fa-info-circle me-2"></i>
                    Wir werden Ihre Ergebnisse auswerten und uns bei Ihnen melden.
                  </p>
                  <p class="mb-0">
                    <i class="fas fa-info-circle me-2"></i>
                    Sie können diese Seite jetzt schließen.
                  </p>
                </div>
              </div>
            </div>
          </div>
          
          <!-- Fehler-Anzeige -->
          <div v-else-if="step === 'error'" class="test-error">
            <div class="card shadow-lg text-center">
              <div class="card-body p-5">
                <div class="error-icon mb-4">
                  <i class="fas fa-exclamation-triangle fa-5x text-danger"></i>
                </div>
                
                <h1 class="display-6 fw-bold mb-3">Fehler</h1>
                <p class="lead mb-4">
                  {{ errorMessage || 'Beim Laden des Tests ist ein Fehler aufgetreten.' }}
                </p>
                
                <button class="btn btn-primary" @click="reloadPage">
                  <i class="fas fa-sync-alt me-2"></i>Erneut versuchen
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions } from 'vuex';
import ApplicantForm from '@/components/applicants/ApplicantForm.vue';

export default {
  name: 'TestTake',
  components: {
    ApplicantForm
  },
  data() {
    return {
      testId: this.$route.params.id,
      test: {},
      applicant: null,
      step: 'loading',
      currentQuestionIndex: 0,
      userAnswers: {},
      remainingTime: 0,
      timer: null,
      loading: true,
      submitting: false,
      errorMessage: '',
      showSubmitConfirm: false
    };
  },
  computed: {
    currentQuestion() {
      return this.test.questions ? this.test.questions[this.currentQuestionIndex] : {};
    },
    progressPercent() {
      if (!this.test.questions || this.test.questions.length === 0) return 0;
      return Math.round(((this.currentQuestionIndex + 1) / this.test.questions.length) * 100);
    },
    unansweredCount() {
      if (!this.test.questions) return 0;
      
      return this.test.questions.filter(question => 
        this.userAnswers[question.id] === undefined || 
        this.userAnswers[question.id] === null ||
        this.userAnswers[question.id] === ''
      ).length;
    }
  },
  created() {
    this.fetchTest();
  },
  methods: {
    ...mapActions({
      fetchTestByHash: 'tests/fetchTestByHash',
      submitTestEntry: 'entries/submitTestEntry'
    }),
    async fetchTest() {
      this.loading = true;
      
      try {
        const test = await this.fetchTestByHash(this.testId);
        this.test = test;
        this.step = 'intro';
      } catch (error) {
        console.error('Fehler beim Laden des Tests:', error);
        this.errorMessage = error.response?.data?.message || 'Der Test konnte nicht geladen werden.';
        this.step = 'error';
      } finally {
        this.loading = false;
      }
    },
    startTest(applicantData) {
      this.applicant = applicantData;
      this.step = 'test';
      
      // Timer starten, falls ein Zeitlimit vorhanden ist
      if (this.test.time_limit) {
        this.remainingTime = this.test.time_limit * 60; // Zeitlimit in Sekunden
        this.startTimer();
      }
    },
    startTimer() {
      this.timer = setInterval(() => {
        this.remainingTime--;
        
        if (this.remainingTime <= 0) {
          clearInterval(this.timer);
          this.timeUp();
        }
      }, 1000);
    },
    timeUp() {
      alert('Die Zeit ist abgelaufen! Der Test wird automatisch abgeschickt.');
      this.submitTest();
    },
    formatTime(seconds) {
      const minutes = Math.floor(seconds / 60);
      const remainingSeconds = seconds % 60;
      return `${minutes}:${remainingSeconds < 10 ? '0' : ''}${remainingSeconds}`;
    },
    nextQuestion() {
      if (this.currentQuestionIndex < this.test.questions.length - 1) {
        this.currentQuestionIndex++;
      }
    },
    previousQuestion() {
      if (this.currentQuestionIndex > 0) {
        this.currentQuestionIndex--;
      }
    },
    goToQuestion(index) {
      this.currentQuestionIndex = index;
    },
    confirmSubmit() {
      this.showSubmitConfirm = true;
    },
    async submitTest() {
      this.submitting = true;
      
      try {
        // Timer stoppen
        if (this.timer) {
          clearInterval(this.timer);
        }
        
        // Antworten aufbereiten
        const formattedAnswers = Object.keys(this.userAnswers).map(questionId => ({
          question_id: questionId,
          answer: this.userAnswers[questionId]
        }));
        
        // Test abschicken
        await this.submitTestEntry({
          testHash: this.testId,
          userData: this.applicant,
          answers: formattedAnswers
        });
        
        this.showSubmitConfirm = false;
        this.step = 'completed';
      } catch (error) {
        console.error('Fehler beim Abschicken des Tests:', error);
        this.errorMessage = error.response?.data?.message || 'Der Test konnte nicht abgeschickt werden.';
        this.showSubmitConfirm = false;
        alert('Beim Abschicken des Tests ist ein Fehler aufgetreten. Bitte versuchen Sie es später erneut.');
      } finally {
        this.submitting = false;
      }
    },
    goBack() {
      window.location.href = '/test/' + this.testId;
    },
    reloadPage() {
      window.location.reload();
    }
  },
  beforeDestroy() {
    // Timer bereinigen
    if (this.timer) {
      clearInterval(this.timer);
    }
  }
};
</script>

<style lang="scss" scoped>
.test-take {
  min-height: 100vh;
  background-color: var(--body-bg);
  
  .card {
    border-radius: 12px;
    border: none;
    overflow: hidden;
  }
  
  .company-logo {
    max-height: 80px;
    max-width: 200px;
    object-fit: contain;
  }
  
  .text-gradient {
    background: linear-gradient(90deg, var(--primary-color) 0%, var(--secondary-color) 100%);
    background-clip: text;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    color: transparent;
  }
  
  .test-info {
    .info-item {
      display: flex;
      align-items: center;
      margin-bottom: 0.75rem;
      
      i {
        color: var(--primary-color);
        margin-right: 0.75rem;
        width: 20px;
        text-align: center;
      }
    }
  }
  
  .timer {
    font-weight: 600;
    color: var(--primary-color);
  }
  
  .question-navigation {
    .question-nav-btn {
      width: 40px;
      height: 40px;
      margin: 0.25rem;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 8px;
    }
  }
  
  .completion-icon, .error-icon {
    margin: 1rem 0;
  }
}
</style> 